/**
 * useIqraHealth — polls /api/iqra/health every 60s and returns the
 * current Iqra LLM availability so the FAB dot can show a live trust
 * signal (green = healthy, amber = degraded, rose = down).
 *
 * Polling is paused when the document is hidden so background tabs
 * don't burn quota. First call fires immediately on mount.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';

const POLL_MS = 60_000;

const STATUS_TONES = {
  healthy:  { dot: 'bg-emerald-400', ring: 'ring-emerald-300/40', glow: 'shadow-emerald-400/40' },
  degraded: { dot: 'bg-amber-400',   ring: 'ring-amber-300/40',   glow: 'shadow-amber-400/40'   },
  down:     { dot: 'bg-rose-500',    ring: 'ring-rose-400/40',    glow: 'shadow-rose-500/40'    },
};

export default function useIqraHealth({ enabled = true } = {}) {
  const [state, setState] = useState({ status: 'healthy', model: null, lastChecked: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get('/iqra/health');
        if (!cancelled) {
          setState({
            status: data.status || 'healthy',
            model: data.model || null,
            lastChecked: data.checked_at || new Date().toISOString(),
          });
        }
      } catch {
        if (!cancelled) {
          setState((s) => ({ ...s, status: 'down', lastChecked: new Date().toISOString() }));
        }
      }
    };

    tick();
    timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled]);

  const tone = STATUS_TONES[state.status] || STATUS_TONES.healthy;
  const label = state.status === 'healthy'
    ? 'AI online'
    : state.status === 'degraded' ? 'AI degraded' : 'AI offline';

  return { ...state, tone, label };
}
